import { saveAs } from "file-saver";

import { MIN_RSA_MODULUS_BITS } from "src/oauthClients/constants";

/**
 * Browser-side key material handling for OAuth client credentials.
 *
 * The private key never leaves the browser on either supply path: we generate
 * it with WebCrypto and hand it to the customer as a PKCS8 PEM download, or the
 * customer brings their own public key and we only convert it to a JWK. Only
 * the public JWK is ever put on the wire.
 */

const PUBLIC_EXPONENT = new Uint8Array([0x01, 0x00, 0x01]);
const ALGORITHM = {
  name: "RSASSA-PKCS1-v1_5",
  hash: "SHA-256",
} as const;

const PRIVATE_RSA_PARAMS = ["d", "p", "q", "dp", "dq", "qi", "oth"] as const;

export type PublicJwk = {
  kty: "RSA";
  n: string;
  e: string;
  alg: "RS256";
  use: "sig";
  kid: string;
};

export type GeneratedKeyPair = {
  publicJwk: PublicJwk;
  privateKeyPem: string;
};

export class KeyMaterialError extends Error {}

const toBase64Url = (bytes: ArrayBuffer): string => {
  const binary = Array.from(new Uint8Array(bytes), (byte) =>
    String.fromCharCode(byte),
  ).join("");
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
};

const fromBase64 = (value: string): Uint8Array => {
  const binary = atob(value);
  return Uint8Array.from(binary, (char) => char.charCodeAt(0));
};

/**
 * RFC 7638 JWK thumbprint. The backend accepts any `kid` matching
 * `[A-Za-z0-9._~-]{1,128}`, and a thumbprint is both in that alphabet and
 * stable for a given key, so re-uploading the same key is recognisably the
 * same credential rather than a new one.
 */
export const jwkThumbprint = async (jwk: {
  e: string;
  n: string;
}): Promise<string> => {
  const canonical = JSON.stringify({ e: jwk.e, kty: "RSA", n: jwk.n });
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(canonical),
  );
  return toBase64Url(digest);
};

const toPublicJwk = async (jwk: JsonWebKey): Promise<PublicJwk> => {
  if (jwk.kty !== "RSA" || !jwk.n || !jwk.e) {
    throw new KeyMaterialError("Only RSA public keys are supported");
  }
  return {
    kty: "RSA",
    n: jwk.n,
    e: jwk.e,
    alg: "RS256",
    use: "sig",
    kid: await jwkThumbprint({ e: jwk.e, n: jwk.n }),
  };
};

const toPem = (der: ArrayBuffer, label: string): string => {
  const binary = Array.from(new Uint8Array(der), (byte) =>
    String.fromCharCode(byte),
  ).join("");
  const body = btoa(binary).match(/.{1,64}/g) ?? [];
  const lines = [
    `-----BEGIN ${label}-----`,
    ...body,
    `-----END ${label}-----`,
    "",
  ];
  return lines.join("\n");
};

const stripPemArmor = (pem: string, label: string): Uint8Array => {
  const match = pem
    .trim()
    .match(
      new RegExp(`-----BEGIN ${label}-----([\\s\\S]+?)-----END ${label}-----`),
    );
  if (!match) {
    throw new KeyMaterialError(
      `Expected a PEM block delimited by BEGIN ${label} and END ${label}`,
    );
  }
  try {
    return fromBase64(match[1].replace(/\s+/g, ""));
  } catch {
    throw new KeyMaterialError("The PEM body is not valid base64");
  }
};

export const generateClientKeyPair = async (): Promise<GeneratedKeyPair> => {
  const keyPair = await crypto.subtle.generateKey(
    {
      ...ALGORITHM,
      modulusLength: MIN_RSA_MODULUS_BITS,
      publicExponent: PUBLIC_EXPONENT,
    },
    true,
    ["sign", "verify"],
  );
  const [publicJwk, privateDer] = await Promise.all([
    crypto.subtle.exportKey("jwk", keyPair.publicKey),
    crypto.subtle.exportKey("pkcs8", keyPair.privateKey),
  ]);
  return {
    publicJwk: await toPublicJwk(publicJwk),
    privateKeyPem: toPem(privateDer, "PRIVATE KEY"),
  };
};

export const publicJwkFromSpkiPem = async (pem: string): Promise<PublicJwk> => {
  if (/-----BEGIN [A-Z ]*PRIVATE KEY-----/.test(pem)) {
    throw new KeyMaterialError(
      "That is a private key. Paste the public key instead; the private key must never leave your machine.",
    );
  }
  const der = stripPemArmor(pem, "PUBLIC KEY");
  const key = await crypto.subtle
    .importKey("spki", der as BufferSource, ALGORITHM, true, ["verify"])
    .catch(() => {
      throw new KeyMaterialError(
        "Could not read that public key. Expected an RSA key in SPKI PEM format.",
      );
    });
  return toPublicJwk(await crypto.subtle.exportKey("jwk", key));
};

export const publicJwkFromJwkJson = async (raw: string): Promise<PublicJwk> => {
  const parsed: unknown = (() => {
    try {
      return JSON.parse(raw);
    } catch {
      throw new KeyMaterialError("That is not valid JSON");
    }
  })();

  if (typeof parsed !== "object" || parsed === null) {
    throw new KeyMaterialError("A JWK must be a JSON object");
  }
  // A JWK Set is the other shape customers routinely paste. Only a set holding
  // exactly one key is unambiguous: picking one out of several would bind the
  // client to a key the customer never chose, and they cannot tell which.
  const candidate = (() => {
    if (
      !("keys" in parsed) ||
      !Array.isArray((parsed as { keys: unknown[] }).keys)
    ) {
      return parsed;
    }
    const { keys } = parsed as { keys: unknown[] };
    if (keys.length !== 1) {
      throw new KeyMaterialError(
        keys.length === 0
          ? "That JWK Set holds no keys"
          : `That JWK Set holds ${keys.length} keys. Paste the single public key you want this client to sign with.`,
      );
    }
    return keys[0];
  })();

  if (typeof candidate !== "object" || candidate === null) {
    throw new KeyMaterialError("A JWK must be a JSON object");
  }
  const jwk = candidate as Record<string, unknown>;

  if (PRIVATE_RSA_PARAMS.some((param) => param in jwk)) {
    throw new KeyMaterialError(
      "That JWK carries private key parameters. Paste the public key only; the private key must never leave your machine.",
    );
  }
  if (jwk.kty !== "RSA") {
    throw new KeyMaterialError("Only RSA keys are supported");
  }
  if (jwk.alg !== undefined && jwk.alg !== "RS256") {
    throw new KeyMaterialError("Only alg=RS256 is supported");
  }
  if (jwk.use !== undefined && jwk.use !== "sig") {
    throw new KeyMaterialError("Only use=sig is supported");
  }
  if (typeof jwk.n !== "string" || typeof jwk.e !== "string") {
    throw new KeyMaterialError("A public RSA JWK must carry n and e");
  }

  const publicJwk = await toPublicJwk({ kty: "RSA", n: jwk.n, e: jwk.e });
  // Honour a kid the customer already uses, as long as the backend accepts it.
  return typeof jwk.kid === "string" && /^[A-Za-z0-9._~-]{1,128}$/.test(jwk.kid)
    ? { ...publicJwk, kid: jwk.kid }
    : publicJwk;
};

export const rsaModulusBits = (jwk: { n: string }): number => {
  const padded = jwk.n + "=".repeat((4 - (jwk.n.length % 4)) % 4);
  // atob rejects a malformed modulus with a DOMException. Callers only handle
  // KeyMaterialError, and anything else reaches the customer as a generic
  // toast instead of an error on the field they pasted into.
  let bytes: Uint8Array;
  try {
    bytes = fromBase64(padded.replace(/-/g, "+").replace(/_/g, "/"));
  } catch {
    throw new KeyMaterialError("The modulus (n) is not valid base64url");
  }
  const leading = bytes.findIndex((byte) => byte !== 0);
  if (leading === -1) return 0;
  return (bytes.length - leading - 1) * 8 + (32 - Math.clz32(bytes[leading]));
};

export const downloadPrivateKeyPem = (
  pem: string,
  clientName: string,
): void => {
  const slug =
    clientName
      .toLowerCase()
      .replace(/[^a-z0-9-_]+/gi, "_")
      .replace(/^_+|_+$/g, "") || "oauth-client";
  saveAs(
    new Blob([pem], { type: "application/x-pem-file" }),
    `${slug}-private-key.pem`,
  );
};
